import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Team } from '@/types';
import {
  TV_NAME_MODE_DEFAULT,
  TV_NAME_MODE_STORAGE_KEY,
  TV_NAME_MODES,
  TV_SCALE_DEFAULT,
  TV_SCALE_KEYS,
  TV_SCALE_MULTIPLIERS,
  TV_SCALE_STORAGE_KEY,
  TV_UPCOMING_COLUMN_MAX_WIDTH,
  TV_UPCOMING_COLUMN_MIN_WIDTH,
  TV_UPCOMING_COLUMN_WIDTH,
  TV_UPCOMING_COLUMN_WIDTH_STORAGE_KEY,
  type TVNameMode,
  type TVScaleKey,
} from './tv-mode.constants';

interface TVScaleContextValue {
  scale: TVScaleKey;
  setScale: (next: TVScaleKey) => void;
  /** Multiplicador efectivo de la escala activa */
  mult: number;
  /** Escala un valor numérico (px) por el multiplicador y redondea */
  s: (value: number) => number;
  /** Modo de nombre de jugador activo */
  nameMode: TVNameMode;
  setNameMode: (next: TVNameMode) => void;
  /** Formatea el nombre de un jugador según el `nameMode` activo */
  formatPlayer: (fullName: string) => string;
  /** Devuelve el nombre del equipo (jugadores unidos por " / ") según `nameMode` */
  getTeamName: (team: Team | undefined | null) => string;
  /**
   * Ancho base (px, pre‑escala) preferido para la columna “Próximos partidos”.
   * Persistido en localStorage; aplicar `s(...)` al renderizar.
   */
  upcomingColumnWidth: number;
  setUpcomingColumnWidth: (next: number) => void;
}

const TVScaleContext = createContext<TVScaleContextValue | null>(null);

function readStoredScale(): TVScaleKey {
  if (typeof window === 'undefined') return TV_SCALE_DEFAULT;
  try {
    const raw = window.localStorage.getItem(TV_SCALE_STORAGE_KEY);
    if (raw && (TV_SCALE_KEYS as readonly string[]).includes(raw)) {
      return raw as TVScaleKey;
    }
  } catch {
    /* localStorage bloqueado: caemos al default */
  }
  return TV_SCALE_DEFAULT;
}

function clampUpcomingWidth(value: number): number {
  if (!Number.isFinite(value)) return TV_UPCOMING_COLUMN_WIDTH;
  return Math.min(
    TV_UPCOMING_COLUMN_MAX_WIDTH,
    Math.max(TV_UPCOMING_COLUMN_MIN_WIDTH, Math.round(value)),
  );
}

function readStoredUpcomingWidth(): number {
  if (typeof window === 'undefined') return TV_UPCOMING_COLUMN_WIDTH;
  try {
    const raw = window.localStorage.getItem(TV_UPCOMING_COLUMN_WIDTH_STORAGE_KEY);
    if (raw) {
      const parsed = Number.parseInt(raw, 10);
      if (Number.isFinite(parsed)) return clampUpcomingWidth(parsed);
    }
  } catch {
    /* localStorage bloqueado: caemos al default */
  }
  return TV_UPCOMING_COLUMN_WIDTH;
}

function readStoredNameMode(): TVNameMode {
  if (typeof window === 'undefined') return TV_NAME_MODE_DEFAULT;
  try {
    const raw = window.localStorage.getItem(TV_NAME_MODE_STORAGE_KEY);
    if (raw && (TV_NAME_MODES as readonly string[]).includes(raw)) {
      return raw as TVNameMode;
    }
  } catch {
    /* localStorage bloqueado: caemos al default */
  }
  return TV_NAME_MODE_DEFAULT;
}

function formatAbbreviated(fullName: string): string {
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 0 || (parts.length === 1 && !parts[0])) return '';
  if (parts.length === 1) return parts[0];
  const first = parts[0];
  const last = parts[parts.length - 1];
  return `${last} ${first.charAt(0).toUpperCase()}.`;
}

function formatFull(fullName: string): string {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0];
  const first = parts[0];
  const last = parts[parts.length - 1];
  return `${first} ${last}`;
}

export function TVScaleProvider({ children }: { children: ReactNode }) {
  const [scale, setScaleState] = useState<TVScaleKey>(() => readStoredScale());
  const [nameMode, setNameModeState] = useState<TVNameMode>(() =>
    readStoredNameMode(),
  );
  const [upcomingColumnWidth, setUpcomingColumnWidthState] = useState<number>(
    () => readStoredUpcomingWidth(),
  );

  useEffect(() => {
    try {
      window.localStorage.setItem(TV_SCALE_STORAGE_KEY, scale);
    } catch {
      /* ignore */
    }
  }, [scale]);

  useEffect(() => {
    try {
      window.localStorage.setItem(TV_NAME_MODE_STORAGE_KEY, nameMode);
    } catch {
      /* ignore */
    }
  }, [nameMode]);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        TV_UPCOMING_COLUMN_WIDTH_STORAGE_KEY,
        String(upcomingColumnWidth),
      );
    } catch {
      /* ignore */
    }
  }, [upcomingColumnWidth]);

  const setScale = useCallback((next: TVScaleKey) => {
    setScaleState(next);
  }, []);

  const setNameMode = useCallback((next: TVNameMode) => {
    setNameModeState(next);
  }, []);

  const setUpcomingColumnWidth = useCallback((next: number) => {
    setUpcomingColumnWidthState(clampUpcomingWidth(next));
  }, []);

  const value = useMemo<TVScaleContextValue>(() => {
    const mult = TV_SCALE_MULTIPLIERS[scale];
    const formatPlayer = (fullName: string) =>
      nameMode === 'full' ? formatFull(fullName) : formatAbbreviated(fullName);
    const getTeamName = (team: Team | undefined | null) => {
      if (!team?.players?.length) return 'Unknown team';
      return team.players
        .map((p) => (p?.name ? formatPlayer(p.name) : ''))
        .filter(Boolean)
        .join(' / ');
    };
    return {
      scale,
      setScale,
      mult,
      s: (value: number) => Math.round(value * mult),
      nameMode,
      setNameMode,
      formatPlayer,
      getTeamName,
      upcomingColumnWidth,
      setUpcomingColumnWidth,
    };
  }, [
    scale,
    setScale,
    nameMode,
    setNameMode,
    upcomingColumnWidth,
    setUpcomingColumnWidth,
  ]);

  return (
    <TVScaleContext.Provider value={value}>{children}</TVScaleContext.Provider>
  );
}

export function useTVScale(): TVScaleContextValue {
  const ctx = useContext(TVScaleContext);
  if (!ctx) {
    /* Fallback seguro: si algún componente se renderiza fuera del provider
       (p.ej. en tests aislados), usamos M sin lanzar — el modo TV completo
       siempre va envuelto por TVScaleProvider en TVModeViewPage. */
    const mult = TV_SCALE_MULTIPLIERS[TV_SCALE_DEFAULT];
    const formatPlayer = (fullName: string) => formatAbbreviated(fullName);
    const getTeamName = (team: Team | undefined | null) => {
      if (!team?.players?.length) return 'Unknown team';
      return team.players
        .map((p) => (p?.name ? formatPlayer(p.name) : ''))
        .filter(Boolean)
        .join(' / ');
    };
    return {
      scale: TV_SCALE_DEFAULT,
      setScale: () => {},
      mult,
      s: (value: number) => Math.round(value * mult),
      nameMode: TV_NAME_MODE_DEFAULT,
      setNameMode: () => {},
      formatPlayer,
      getTeamName,
      upcomingColumnWidth: TV_UPCOMING_COLUMN_WIDTH,
      setUpcomingColumnWidth: () => {},
    };
  }
  return ctx;
}
